import * as React from "react";
import PropTypes, { array } from "prop-types";
import {
  Box,
  Paper,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Edit from "@mui/icons-material/Edit";
import SearchBar from "./SearchBar";
import { styled, alpha } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import LoadingScreen from "../LoadingScreen";
import { useData } from "../../contexts/DataContext/DataContextt";
import ImageNotSupported from "@mui/icons-material/ImageNotSupported";
import { isImage } from "../../types/constant";

function Row({
  content,
  headers,
  row,
  rowActions,
  collapse,
  showEdit,
  isValueOpen,
  isEditOpen,
  onToggleValue,
  onToggleEdit,
  showImage = true,
}) {
  //const [openValue, setOpenValue] = React.useState(false);
  //const [openDefaultEdit, setOpenDefaultEdit] = React.useState(false);
  const newContent =
    content &&
    React.cloneElement(content, {
      props: {
        ...content.props.props,
        row,
      },
    });

  const isContentOpen = (row.isSelectionNew || isEditOpen) ?? false;
  const rowValues = Array.isArray(row.values)
    ? row.values
    : row.values?.split("|");
  const rowUnit = Array.isArray(row.unit) ? row.unit : row.unit?.split("|");

  return (
    <React.Fragment>
      <StyledTableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        {collapse && (
          <StyledTableCell width="10px">
            {(rowValues?.length > 0 || rowUnit?.length > 0) && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={onToggleValue}
              >
                {isValueOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            )}
          </StyledTableCell>
        )}
        {headers?.map((h, i) => {
          //const { , className , classDisplayName } = classes.find(f => f.id === id) ?? {};
          const {
            isPropArray,
            code,
            displayName,
            description,
            type,
            allStatus,
          } = Array.isArray(h.prop)
            ? {
                isPropArray: true,
                displayName: row[h.prop[0]?.id] ?? "",
                code: row[h.prop[1]?.id] ?? "",
                description: row[h.prop[2]?.id] ?? "",
                type: row[h.prop[3]?.id] ?? undefined,
                allStatus:
                  h.prop[4]?.id.map((m) => row[m] ?? true) ?? undefined,
              }
            : {};
          let isArray = h.isArray ?? false;
          let cellValue = h.format
            ? String(row[h.prop]).padStart(h.format.length, "0")
            : row[h.prop];

          const isAllStatusActive = allStatus?.every(
            (status) => status === true,
          );

          if (Array.isArray(cellValue)) {
            isArray = true;
          } else {
            cellValue = isArray ? cellValue?.split("|") : cellValue;
          }

          return isPropArray ? (
            <StyledTableCell
              sx={{
                maxWidth: "800px", // max width
                whiteSpace: "nowrap", // single-line ellipsis
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              key={`table_row_${i}`}
            >
              <CellCard
                displayName={displayName}
                description={description}
                code={code}
                type={type}
                isAllStatusActive={isAllStatusActive}
              />
            </StyledTableCell>
          ) : isArray ? (
            <LimitedArrayTableCell
              key={i}
              value={cellValue}
              limit={2} // change limit if needed
            />
          ) : (
            <StyledTableCell
              sx={h?.width ? { width: h.width } : {}}
              component="th"
              scope="row"
              key={i}
            >
              {h.isImage && showImage && isImage(String(cellValue)) ? (
                <img
                  srcSet={``}
                  width="28"
                  src={`${cellValue ?? ""}`}
                  alt={`${cellValue ?? ""}`}
                  loading="lazy"
                />
              ) : h.isImage && showImage ? (
                <ImageNotSupported color="info" />
              ) : (
                !h.isImage && (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    <strong>
                      {cellValue != null ? String(cellValue) : ""}
                    </strong>
                  </Typography>
                )
              )}
            </StyledTableCell>
          );
        })}
        {
          <StyledTableCell sx={{ width: "10px" }} component="th" scope="row">
            <Stack direction="row" spacing={1} className="row-actions">
              {/* Default grid action */}
              {showEdit && (
                <Tooltip
                  title={!isContentOpen ? `Show edit panel` : `Hide edit panel`}
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    className="row-action-btn"
                    onClick={onToggleEdit}
                  >
                    {!isContentOpen ? (
                      <KeyboardArrowRight />
                    ) : (
                      <KeyboardArrowUpIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {/* User-provided actions */}
              {typeof rowActions === "function" ? rowActions(row) : rowActions}
            </Stack>
          </StyledTableCell>
        }
      </StyledTableRow>

      <>
        {(rowValues?.length > 0 || rowUnit?.length > 0) && (
          <TableRow>
            <TableCell
              style={{ paddingBottom: 0, paddingTop: 0 }}
              colSpan={headers?.length + 2}
            >
              <Collapse in={isValueOpen} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1, marginTop: 2 }}>
                  <Table size="small" aria-label="purchases">
                    <TableBody>
                      {rowValues?.map((rv, rvi) => {
                        return (
                          <StyledTableRow key={`value_row_${rvi}`}>
                            <StyledTableCell>
                              <CellCard
                                key={`value_index_${rvi}`}
                                displayName={rv.valueDisplayName}
                                isDefault={rv.isDefault}
                                description={undefined}
                                code={rv.valueCode}
                                type={undefined}
                                isAllStatusActive={undefined}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                      {rowUnit?.map((rv, rvi) => {
                        return (
                          <StyledTableRow key={`unit_row_${rvi}`}>
                            <StyledTableCell>
                              <CellCard
                                key={`unit_index_${rvi}`}
                                displayName={rv.unitName}
                                isDefault={rv.isDefault}
                                description={undefined}
                                code={rv.unitCode}
                                type={undefined}
                                isAllStatusActive={undefined}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
        {content && collapse && (
          <TableRow>
            <TableCell
              style={{ paddingBottom: 0, paddingTop: 0 }}
              colSpan={headers?.length + 2}
            >
              <Collapse in={isContentOpen} timeout="auto" unmountOnExit>
                <Box sx={{ marginTop: 2, marginBottom: 3 }}>{newContent}</Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    </React.Fragment>
  );
}

const LimitedArrayTableCell = ({ value, limit = 2, renderItem, ...props }) => {
  if (!Array.isArray(value)) {
    return <StyledTableCell {...props}>{value ?? ""}</StyledTableCell>;
  }

  const visibleItems = value.slice(0, limit);
  const hiddenCount = value.length - limit;

  return (
    <StyledTableCell {...props}>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {visibleItems.map((item, index) => (
          <React.Fragment key={index}>
            {renderItem ? (
              renderItem(item)
            ) : (
              <Chip size="small" label={String(item)} />
            )}
          </React.Fragment>
        ))}

        {hiddenCount > 0 && (
          <Tooltip title={value.map((v) => String(v)).join(", ")}>
            <Chip size="small" label={`+${hiddenCount} more`} />
          </Tooltip>
        )}
      </Box>
    </StyledTableCell>
  );
};

export default function TabulatedGridCollapsible({
  headers,
  data,
  pagination,
  content,
  search,
  rowActions,
  controlledExpanded,
  onToggleRow,
  showHeader = true,
  collapse = true,
  tableStyle,
}) {
  const [expandedRows, setExpandedRows] = React.useState({
    values: new Set(),
    edit: new Set(),
  });
  const { loading } = useData();
  const state = controlledExpanded ?? expandedRows;

  const toggleRow = (type, id) => {
    if (onToggleRow) {
      onToggleRow(type, id);
      return;
    }

    setExpandedRows((prev) => {
      const newSet = new Set(prev[type]);

      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);

      return {
        ...prev,
        [type]: newSet,
      };
    });
  };

  return (
    <>
      <SearchBar
        sx={{ width: "500px" }}
        placeHolder={search?.placeHolder}
        handleSearchClick={search?.handleSearchClick}
        onKeyDown={search?.onKeyDown}
      />
      {
        <Box
          sx={{
            ...(tableStyle?.maxHeight && {
              maxHeight: tableStyle?.maxHeight,
              overflowY: "auto",
            }),
          }}
        >
          <TableContainer component={Paper} sx={{ marginTop: 3 }}>
            <Table aria-label="collapsible table">
              {showHeader && (
                <TableHead>
                  <TableRow>
                    {collapse && <StyledTableCell width="10" />}
                    {headers?.map((h, i) => (
                      <StyledTableCell
                        sx={{
                          width: h?.width || "10px", // fallback width
                        }}
                        key={i}
                      >
                        {h.text}
                      </StyledTableCell>
                    ))}
                    <StyledTableCell
                      sx={{ width: "10px", border: "solid 1px" }}
                    ></StyledTableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {data?.length > 0 ? (
                  data?.map((row, index) => (
                    <Row
                      key={`row_${index}`}
                      content={content}
                      headers={headers}
                      row={row}
                      showEdit={tableStyle?.showEdit ?? true}
                      rowActions={rowActions}
                      collapse={collapse}
                      isValueOpen={state.values.has(row.id)}
                      isEditOpen={state.edit.has(row.id)}
                      showImage={tableStyle?.showImage}
                      onToggleValue={() => toggleRow("values", row.id)}
                      onToggleEdit={() => toggleRow("edit", row.id)}
                    />
                  ))
                ) : (
                  <>
                    <TableRow>
                      <TableCell align="center" colSpan={headers?.length + 3}>
                        No data
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      }
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={pagination?.totalRows ?? 0}
          page={pagination?.page}
          rowsPerPage={pagination?.rowsPerPage}
          onPageChange={pagination?.handleChangePage}
          onRowsPerPageChange={pagination?.handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page" // optional custom label
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count}`
          }
          sx={{
            "& .MuiTablePagination-toolbar": {
              //border: (theme)=> `solid 1px ${theme.palette.grey[200]}`,
              backgroundColor: (theme) => theme.palette.grey[200], // same as header
              display: "flex",
              justifyContent: "flex-end", // moves the toolbar to the right
              gap: 2,
            },
            "& .MuiTablePagination-selectLabel": {
              marginRight: 1, // optional spacing
              marginTop: 2,
            },
            "& .MuiTablePagination-displayedRows": {
              marginLeft: 1, // optional spacing,
              marginTop: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                color: (theme) => theme.palette.common.black, // match header text color
                fontWeight: 600,
              },
            "& .MuiTablePagination-actions button": {
              color: (theme) => theme.palette.common.black, // pagination arrows
            },
          }}
        />
      )}
    </>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    //color: theme.palette.common.black
    backgroundColor: theme.palette.grey[200],
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    //backgroundColor: theme.palette.action.hover
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.grey[400], 0.1), // lighter version of primary
    cursor: "pointer", // optional: show pointer
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "& .row-action-btn": {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },

  "&:hover .row-action-btn": {
    opacity: 1,
  },
}));

const CellCard = ({
  displayName,
  description,
  code,
  type,
  isAllStatusActive,
  isDefault = false,
}) => {
  return (
    <Box>
      <Box display="flex" gap={1}>
        <Typography
          gutterBottom
          sx={(theme) => ({
            fontWeight: 600,
            color: theme.palette.text.main, // theme is available here
          })}
        >
          {code}
        </Typography>
        {isAllStatusActive !== undefined && (
          <Chip
            label={isAllStatusActive ? "Active" : "Inactive"}
            color={isAllStatusActive ? "info" : "warning"}
            size="small"
          />
        )}
        {isDefault && (
          <Chip
            label={isDefault ? "Default" : ""}
            color={isDefault ? "info" : ""}
            size="small"
          />
        )}
      </Box>
      <Typography
        //variant="body2"
        sx={{ mb: 2, color: "text.secondary" }}
      >
        {displayName} {type !== undefined ? ` - ${type}` : ""}
      </Typography>

      {description && (
        <Typography
          noWrap
          //variant="body1"
          sx={{
            color: "text.primary",
            //whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};
